(() => {
  'use strict';

  class ProductDetails {
    constructor(root) {
      this.root = root;
      this.scope = root.closest('.shopify-section') || root.parentElement || root;
      this.controller = new AbortController();
      this.observer = null;
      this.pointerStart = null;
      this.lightboxTrigger = null;
      this.isSubmitting = false;

      const dataElement = this.scope.querySelector('[data-product-json]');
      if (!window.Triple3App || !dataElement) {
        throw new Error('Product details dependencies are unavailable.');
      }

      this.data = JSON.parse(dataElement.textContent);
      this.strings = window.Triple3App.getConfig().strings;
      this.variants = Array.isArray(this.data.variants) ? this.data.variants : [];
      this.form = root.querySelector('form.product-form');
      this.variantInput = root.querySelector('[data-variant-id]');
      this.quantityInput = root.querySelector('[data-product-quantity]');
      this.sellingPlanSelect = root.querySelector('[data-selling-plan]');
      this.submitButton = root.querySelector('[data-product-submit]');
      this.buyNowButton = root.querySelector('[data-product-buy-now]');
      this.submitText = root.querySelector('[data-product-submit-text]');
      this.errorElement = root.querySelector('[data-product-error]');
      this.lightbox = this.scope.querySelector('[data-product-lightbox]');
      this.sticky = this.scope.querySelector('[data-product-sticky]');
      this.optionGroups = Array.from(root.querySelectorAll('[data-option-index]'));
      this.selectedVariant =
        this.variants.find(
          (variant) => String(variant.id) === String(this.variantInput?.value)
        ) || this.variants[0];

      this.bindEvents();
      this.updateVariant(this.selectedVariant, false);
      this.syncWishlist();
      this.observePrimaryAction();
      this.observeStickyHeader();
      this.listen(document, 'shopify:section:load', () => this.observeStickyHeader());
      this.listen(document, 'cart:busy', () => this.syncPurchaseControls());
      root.dataset.productInitialized = 'true';
    }

    listen(target, eventName, handler, options = {}) {
      if (!target) return;
      target.addEventListener(eventName, handler, {
        ...options,
        signal: this.controller.signal
      });
    }

    selectedOptions() {
      return this.optionGroups.map(
        (group) =>
          group.querySelector('[aria-pressed="true"]')?.dataset.optionValue || ''
      );
    }

    findVariant(options) {
      return this.variants.find((variant) =>
        variant.options.every((option, index) => option === options[index])
      );
    }

    showMedia(mediaId) {
      if (!mediaId) return;
      this.root.querySelectorAll('[data-media-id]').forEach((panel) => {
        panel.hidden = String(panel.dataset.mediaId) !== String(mediaId);
      });
      this.root.querySelectorAll('[data-media-thumbnail]').forEach((thumbnail) => {
        const active =
          String(thumbnail.dataset.mediaThumbnail) === String(mediaId);
        thumbnail.classList.toggle('product__thumb--active', active);
        thumbnail.setAttribute('aria-current', String(active));
      });
    }

    updateOptionAvailability() {
      const selected = this.selectedOptions();
      this.optionGroups.forEach((group, groupIndex) => {
        group.querySelectorAll('[data-option-value]').forEach((button) => {
          const candidateOptions = selected.slice();
          candidateOptions[groupIndex] = button.dataset.optionValue;
          const possible = this.variants.some(
            (variant) =>
              variant.available &&
              variant.options.every(
                (option, index) =>
                  index === groupIndex || option === candidateOptions[index]
              ) &&
              variant.options[groupIndex] === candidateOptions[groupIndex]
          );
          button.disabled =
            !possible && button.getAttribute('aria-pressed') !== 'true';
        });
      });
    }

    updateSellingPlans() {
      if (!this.sellingPlanSelect || !this.selectedVariant) return;
      const allowed = (
        this.data.variantSellingPlans[String(this.selectedVariant.id)] || []
      ).map(String);

      this.sellingPlanSelect
        .querySelectorAll('option[value]:not([value=""])')
        .forEach((option) => {
          option.disabled = !allowed.includes(option.value);
        });

      if (
        this.sellingPlanSelect.value &&
        !allowed.includes(this.sellingPlanSelect.value)
      ) {
        this.sellingPlanSelect.value = '';
      }
    }

    updateVariant(variant, updateUrl = true) {
      this.selectedVariant = variant;
      const available = Boolean(variant?.available);
      if (this.variantInput) this.variantInput.value = variant?.id || '';

      const price = this.root.querySelector('[data-product-price]');
      const compare = this.root.querySelector('[data-product-compare-price]');
      const saving = this.root.querySelector('[data-product-saving]');
      const stickyPrice = this.scope.querySelector('[data-sticky-price]');
      const stock = this.root.querySelector('[data-product-stock]');
      const stockText = this.root.querySelector('[data-product-stock-text]');
      const sku = this.root.querySelector('[data-product-sku]');

      if (variant) {
        const compareAtPrice = Number(variant.compareAtPrice || 0);
        const currentPrice = Number(variant.price || 0);
        const onSale = compareAtPrice > currentPrice;

        if (price) price.textContent = window.Triple3App.formatPrice(currentPrice);
        if (stickyPrice) {
          stickyPrice.textContent = window.Triple3App.formatPrice(currentPrice);
        }
        if (compare) {
          compare.hidden = !onSale;
          compare.textContent = onSale
            ? window.Triple3App.formatPrice(compareAtPrice)
            : '';
        }
        if (saving) {
          saving.hidden = !onSale;
          saving.textContent = onSale
            ? this.strings.savePercentage.replace(
                '[percent]',
                String(
                  Math.round(
                    ((compareAtPrice - currentPrice) / compareAtPrice) * 100
                  )
                )
              )
            : '';
        }
        if (variant.featuredMediaId) this.showMedia(variant.featuredMediaId);
        if (sku) {
          sku.hidden = !variant.sku;
          const value = sku.querySelector('span');
          if (value) value.textContent = variant.sku || '';
        }
      }

      stock?.classList.toggle('product__stock--out', !available);
      if (stockText) {
        stockText.textContent = available
          ? this.strings.inStock
          : this.strings.outOfStock;
      }
      if (this.submitText) {
        this.submitText.textContent = available
          ? this.strings.addToCart
          : variant
            ? this.strings.outOfStock
            : this.strings.unavailable;
      }
      this.updateQuantityRules();
      this.syncPurchaseControls();
      this.updateSellingPlans();
      this.updateOptionAvailability();

      if (updateUrl && variant?.id) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({ variantId: variant.id }, '', url);
      }
    }

    syncPurchaseControls() {
      const disabled = !this.selectedVariant?.available || window.Triple3App.isCartBusy();
      [this.submitButton, this.buyNowButton, ...this.scope.querySelectorAll('[data-sticky-add], [data-sticky-buy]')]
        .filter(Boolean).forEach((button) => { button.disabled = disabled; });
    }

    /** @param {number | string} value @returns {number} */
    normalizeQuantity(value) {
      const minimum = Math.max(1, Number(this.quantityInput?.min) || 1);
      const increment = Math.max(1, Number(this.quantityInput?.step) || 1);
      const maximum = Math.max(minimum, Number(this.quantityInput?.max) || Number(this.data.maxQuantity) || 99);
      const parsed = Number(value);
      const bounded = Math.min(maximum, Math.max(minimum, Number.isFinite(parsed) ? parsed : minimum));
      return minimum + Math.floor((bounded - minimum) / increment) * increment;
    }

    updateQuantityRules() {
      if (!this.quantityInput) return;
      const rules = this.selectedVariant?.quantityRule || {};
      const minimum = Math.max(1, Number(rules.min) || 1);
      const maximum = Math.max(minimum, Math.min(Number(rules.max) || Infinity, Number(this.data.maxQuantity) || 99));
      this.quantityInput.min = String(minimum);
      this.quantityInput.max = String(maximum);
      this.quantityInput.step = String(Math.max(1, Number(rules.increment) || 1));
      this.quantityInput.value = String(this.normalizeQuantity(this.quantityInput.value));
    }

    async addSelectedVariant({ checkout = false } = {}) {
      if (!this.selectedVariant?.available || this.isSubmitting || window.Triple3App.isCartBusy()) return;
      this.isSubmitting = true;
      if (this.errorElement) this.errorElement.hidden = true;
      try {
        const fields = this.form ? new FormData(this.form) : null;
        /** @type {Record<string, string>} */
        const properties = Object.create(null);
        fields?.forEach((value, name) => {
          const match = /^properties\[(.+)\]$/.exec(name);
          if (match && typeof value === 'string') properties[match[1]] = value;
        });
        const quantity = this.normalizeQuantity(this.quantityInput?.value || this.selectedVariant.quantityRule?.min || 1);
        if (this.quantityInput) this.quantityInput.value = String(quantity);
        const cart = await window.Triple3App.addToCart(
          this.selectedVariant.id, quantity, properties,
          this.sellingPlanSelect?.value || fields?.get('selling_plan') || null
        );
        if (!cart) {
          if (this.errorElement) {
            this.errorElement.textContent = window.Triple3App.getCartError() || this.strings.cartError;
            this.errorElement.hidden = false;
          }
          return;
        }
        if (checkout) window.location.assign(this.data.checkoutUrl);
      } catch (error) {
        if (this.errorElement) {
          this.errorElement.textContent = window.Triple3App.getCartError() || this.strings.cartError;
          this.errorElement.hidden = false;
        }
        window.Triple3App.showToast(this.strings.cartError, 'error');
      } finally {
        this.isSubmitting = false;
        this.syncPurchaseControls();
      }
    }

    openLightbox(trigger) {
      if (!this.lightbox) return;
      this.lightboxTrigger = trigger;
      const image = this.lightbox.querySelector('[data-lightbox-image]');
      if (image) {
        image.src = trigger.dataset.lightboxSrc || image.dataset.lightboxSrc;
        image.alt =
          trigger.querySelector('img')?.alt || this.data.productTitle || '';
      }
      this.lightbox.hidden = false;
      this.lightbox.removeAttribute('inert');
      this.lightbox.classList.add('is-open');
      this.lightbox.setAttribute('aria-hidden', 'false');
      document.body.classList.add('overflow-hidden');
      this.lightbox.querySelector('.lightbox__close')?.focus();
    }

    closeLightbox() {
      if (!this.lightbox?.classList.contains('is-open')) return;
      this.lightbox.classList.remove('is-open');
      this.lightbox.setAttribute('aria-hidden', 'true');
      this.lightbox.setAttribute('inert', '');
      this.lightbox.hidden = true;
      document.body.classList.remove('overflow-hidden');
      this.lightboxTrigger?.focus();
    }

    syncWishlist() {
      const button = this.root.querySelector('[data-product-wishlist]');
      if (!button) return;
      const saved = window.Triple3App.isWishlisted(button.dataset.productId);
      button.classList.toggle('is-active', saved);
      button.setAttribute('aria-pressed', String(saved));
      const label = button.querySelector('[data-wishlist-label]');
      if (label) {
        label.textContent = saved
          ? this.strings.wishlistSaved
          : this.strings.wishlistSave;
      }
    }

    observePrimaryAction() {
      if (!this.sticky || !this.submitButton || !('IntersectionObserver' in window)) {
        return;
      }
      const update = () => {
        const header = document.querySelector('.shopify-section-group-header-group.section-header');
        const headerBottom = header && getComputedStyle(header).position === 'sticky'
          ? Math.max(0, header.getBoundingClientRect().bottom)
          : 0;
        const visible = window.matchMedia('(max-width: 1023px)').matches &&
          this.submitButton.getBoundingClientRect().bottom <= headerBottom;
        if (this.sticky.hidden === !visible) return;
        if (!visible && this.sticky.contains(document.activeElement)) {
          this.submitButton.focus({ preventScroll: true });
        }
        this.sticky.hidden = !visible;
        this.sticky.toggleAttribute('inert', !visible);
        this.sticky.setAttribute('aria-hidden', String(!visible));
        this.sticky.classList.toggle('is-visible', visible);
      };
      this.observer = new IntersectionObserver(update);
      this.observer.observe(this.submitButton);
      this.listen(window, 'scroll', update, { passive: true });
      this.listen(window, 'resize', update, { passive: true });
      update();
    }

    observeStickyHeader() {
      this.headerObserver?.disconnect();
      if (this.root.dataset.stickyGallery !== 'true') return;

      const header = document.querySelector('.shopify-section-group-header-group.section-header');
      const updateOffset = () => {
        const sticky = header && getComputedStyle(header).position === 'sticky';
        const height = sticky ? header.getBoundingClientRect().height : 0;
        this.root.style.setProperty('--product-sticky-header-height', `${height}px`);
      };
      updateOffset();
      if (header && typeof ResizeObserver === 'function') {
        this.headerObserver = new ResizeObserver(updateOffset);
        this.headerObserver.observe(header);
      }
    }

    bindEvents() {
      this.optionGroups.forEach((group) => {
        this.listen(group, 'click', (event) => {
          const button = event.target.closest('[data-option-value]');
          if (!button || button.disabled) return;
          group.querySelectorAll('[data-option-value]').forEach((option) => {
            const selected = option === button;
            option.classList.toggle('is-selected', selected);
            option.setAttribute('aria-pressed', String(selected));
          });
          const selectedLabel = group.querySelector('[data-selected-option]');
          if (selectedLabel) {
            selectedLabel.textContent = button.dataset.optionValue;
          }
          this.updateVariant(this.findVariant(this.selectedOptions()));
        });
      });

      this.root.querySelectorAll('[data-media-thumbnail]').forEach((thumbnail) => {
        this.listen(thumbnail, 'click', () =>
          this.showMedia(thumbnail.dataset.mediaThumbnail)
        );
      });

      this.root.querySelectorAll('[data-quantity-change]').forEach((button) => {
        this.listen(button, 'click', () => {
          if (!this.quantityInput) return;
          const increment = Math.max(1, Number(this.quantityInput.step) || 1);
          const next = this.normalizeQuantity(Number(this.quantityInput.value || this.quantityInput.min || 1) + Number(button.dataset.quantityChange) * increment);
          this.quantityInput.value = String(next);
        });
      });

      this.listen(this.quantityInput, 'change', () => {
        this.quantityInput.value = String(this.normalizeQuantity(this.quantityInput.value));
      });

      this.listen(this.form, 'submit', (event) => {
        event.preventDefault();
        this.addSelectedVariant();
      });
      this.listen(this.buyNowButton, 'click', () =>
        this.addSelectedVariant({ checkout: true })
      );
      this.listen(this.scope.querySelector('[data-sticky-add]'), 'click', () =>
        this.addSelectedVariant()
      );
      this.listen(this.scope.querySelector('[data-sticky-buy]'), 'click', () =>
        this.addSelectedVariant({ checkout: true })
      );

      this.listen(
        this.root.querySelector('[data-product-wishlist]'),
        'click',
        (event) => {
          window.Triple3App.toggleWishlist(event.currentTarget.dataset.productId);
          this.syncWishlist();
        }
      );

      this.listen(
        this.root.querySelector('[data-product-share]'),
        'click',
        async () => {
          try {
            if (navigator.share) {
              await navigator.share({
                title: this.data.productTitle,
                url: window.location.href
              });
            } else {
              await navigator.clipboard.writeText(window.location.href);
              window.Triple3App.showToast(this.data.copySuccess);
            }
          } catch (error) {
            if (!(error instanceof DOMException && error.name === 'AbortError')) {
              window.Triple3App.showToast(this.data.shareError, 'error');
            }
          }
        }
      );

      this.root.querySelectorAll('[data-lightbox-trigger]').forEach((trigger) => {
        this.listen(trigger, 'click', () => this.openLightbox(trigger));
      });
      this.lightbox?.querySelectorAll('[data-lightbox-close]').forEach((button) => {
        this.listen(button, 'click', () => this.closeLightbox());
      });
      this.listen(this.lightbox, 'keydown', (event) => {
        if (event.key === 'Escape') this.closeLightbox();
        if (event.key === 'Tab') {
          event.preventDefault();
          this.lightbox.querySelector('.lightbox__close')?.focus();
        }
      });

      const media = this.root.querySelector('[data-product-media]');
      this.listen(media, 'pointerdown', (event) => {
        this.pointerStart = event.clientX;
      });
      this.listen(media, 'pointerup', (event) => {
        if (
          this.pointerStart == null ||
          Math.abs(event.clientX - this.pointerStart) < 50
        ) {
          this.pointerStart = null;
          return;
        }
        const panels = Array.from(this.root.querySelectorAll('[data-media-id]'));
        const currentIndex = panels.findIndex((panel) => !panel.hidden);
        const direction = event.clientX < this.pointerStart ? 1 : -1;
        const next =
          panels[(currentIndex + direction + panels.length) % panels.length];
        if (next) this.showMedia(next.dataset.mediaId);
        this.pointerStart = null;
      });

      this.listen(window, 'popstate', () => {
        const id = new URL(window.location.href).searchParams.get('variant');
        const variant = this.variants.find(
          (candidate) => String(candidate.id) === id
        );
        if (!variant) return;
        this.optionGroups.forEach((group, index) => {
          group.querySelectorAll('[data-option-value]').forEach((button) => {
            const selected =
              button.dataset.optionValue === variant.options[index];
            button.classList.toggle('is-selected', selected);
            button.setAttribute('aria-pressed', String(selected));
          });
          const selectedLabel = group.querySelector('[data-selected-option]');
          if (selectedLabel) selectedLabel.textContent = variant.options[index];
        });
        this.updateVariant(variant, false);
      });
    }

    destroy() {
      this.controller.abort();
      this.observer?.disconnect();
      this.headerObserver?.disconnect();
      this.closeLightbox();
      this.root.removeAttribute('data-product-initialized');
    }
  }

  function initializeProducts(scope = document) {
    const roots = [];
    if (scope instanceof Element && scope.matches('[data-product-section]')) {
      roots.push(scope);
    }
    roots.push(...scope.querySelectorAll('[data-product-section]'));

    roots.forEach((root) => {
      root._triple3Product?.destroy();
      try {
        root._triple3Product = new ProductDetails(root);
      } catch (error) {
        root.removeAttribute('data-product-initialized');
        console.error('Unable to initialize Product details.', error);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => initializeProducts(document),
      { once: true }
    );
  } else {
    initializeProducts(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    initializeProducts(event.target);
  });
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-product-section]').forEach((root) => {
      root._triple3Product?.destroy();
    });
  });
})();
